import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';

const SectionHeader = ({ title, icon: Icon, color }) => (
  <div className="flex items-center gap-2 mb-4 mt-8 first:mt-0">
    <div className={cn("p-1.5 rounded-lg", color)}>
      <SafeIcon icon={Icon} className="w-4 h-4" />
    </div>
    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">{title}</h3>
  </div>
);

const InsightPanel = ({ insights }) => {
  if (!insights) return null;

  return (
    <div className="space-y-8 bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 p-5 sm:p-6 shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden">
      <div className="max-h-[calc(100vh-12rem)] lg:max-h-[calc(100vh-16rem)] overflow-y-auto custom-scrollbar pr-1">
        {/* 1. Top Traffic Assets */}
        <div>
          <SectionHeader title="Top Traffic Assets" icon={FiIcons.FiGlobe} color="bg-indigo-100 text-indigo-600" />
          <div className="space-y-2">
            {insights.topUrls.map((u, i) => (
              <div key={i} className="group p-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-100 dark:border-slate-700/50 hover:border-indigo-300 transition-all">
                <div className="flex justify-between items-start gap-3 mb-1.5">
                  <p className="text-[10px] font-mono text-slate-500 truncate flex-1" title={u.url}>
                    {u.url.replace(/^https?:\/\/(www\.)?/, '')}
                  </p>
                  <span className="text-[10px] font-black text-indigo-600 whitespace-nowrap">{u.totalClicks.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, (u.totalClicks / (insights.topUrls[0].totalClicks || 1)) * 100)}%` }} />
                  </div>
                  <span className="text-[9px] text-slate-400 font-black">{u.keywords.length} KW</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 2. Market Share */}
        <div>
          <SectionHeader title="Market Dominance" icon={FiIcons.FiPieChart} color="bg-emerald-100 text-emerald-600" />
          <div className="space-y-3">
            {insights.marketShare.map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-black text-slate-500">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="font-black truncate text-slate-700 dark:text-slate-200 uppercase tracking-tight">{d.domain}</span>
                    <span className="text-emerald-600 font-black">{((d.clicks / insights.marketShare.reduce((a, b) => a + b.clicks, 0)) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(d.clicks / insights.marketShare[0].clicks) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 3. High CPC */}
        <div>
          <SectionHeader title="CPC Opportunities" icon={FiIcons.FiDollarSign} color="bg-amber-100 text-amber-600" />
          <div className="grid grid-cols-1 gap-2">
            {insights.cpcLeaders.map((k, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 bg-amber-50/50 dark:bg-amber-900/10 rounded-xl border border-amber-100/50 dark:border-amber-900/20">
                <span className="text-[11px] font-bold text-slate-600 dark:text-slate-300 truncate mr-2">{k.keyword}</span>
                <span className="text-[11px] font-black text-amber-600 whitespace-nowrap">${k.cpc.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightPanel;